import { collection, doc, getDoc, query, getDocs, where, addDoc, serverTimestamp } from 'firebase/firestore'
import db from '../../../../services/firebase'
import { User } from 'firebase/auth'

/**
 * Fetch user data from Firestore
 */
export async function fetchUserDataFromFirestore(uid: string) {
  const userDocRef = doc(collection(db, 'users'), uid)

  try {
    const userDocSnapshot = await getDoc(userDocRef)

    if (userDocSnapshot.exists()) {
      return userDocSnapshot.data()
    } else {
      console.error('User data not found in Firestore')
      return {}
    }
  } catch (error) {
    console.error('Error fetching user data from Firestore:', error)
    throw error
  }
}

/**
 * Fetch personal data from Firestore
 */
export async function fetchPersonalDataFromFirestore(tenantId: string, userId: string, email: string) {
  try {
    const personalDocSnapshot = db
      .collection('tenants')
      .doc(tenantId)
      .collection('employees')
      .where('uid', '==', userId)
      .get()
    if ((await personalDocSnapshot).docs.length > 0) {
      return (await personalDocSnapshot).docs[0].data()
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

/**
 * Check if email is already registered with a different provider
 */
export async function checkEmailProvider(email: string, expectedProvider: string): Promise<boolean> {
  const usersCollectionRef = collection(db, "users")
  const emailQuery = query(usersCollectionRef, where("email", "==", email))
  const emailQuerySnapshot = await getDocs(emailQuery)
  
  if (!emailQuerySnapshot.empty) {
    const userData = emailQuerySnapshot.docs[0].data()
    if (userData.provider !== expectedProvider) {
      return false // Email exists with different provider
    }
  }
  return true // Email doesn't exist or exists with same provider
}

/**
 * Create activity log entry
 */
export async function createActivityLog(user: User, tenantId: string, additionalUserData: any) {
  const tenantRef = doc(db, 'tenants', tenantId)
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const activitiesRef = collection(tenantRef, 'activities')
  await addDoc(activitiesRef, {
    message: `${user.displayName} has logged in the SecureSlate`,
    timestamp: serverTimestamp(),
    tenantId: tenantId,
    uid: user.uid,
    action: 'User login',
    target: 'Authentication',
    email: user.email,
    username: user.displayName,
    date: todaysDate,
  })
}

/**
 * Create logout activity log entry
 */
export async function createLogoutActivityLog(user: any, tenantId: string) {
  const tenantRef = doc(db, 'tenants', tenantId)
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const activitiesRef = collection(tenantRef, 'activities')
  await addDoc(activitiesRef, {
    message: `${user.userName || user.displayName} has logged out of SecureSlate`,
    timestamp: serverTimestamp(),
    tenantId: tenantId,
    uid: user.uid,
    action: 'User logout',
    target: 'Authentication',
    email: user.email,
    username: user.userName || user.displayName,
    date: todaysDate,
  })
}

/**
 * Navigate user based on role and account type
 */
export function navigateUserByRole(userPersonaData: any, navigate: Function) {
  if (userPersonaData?.account_type === 'trust') {
    navigate('/trust-center')
  } else {
    switch (userPersonaData.role) {
      case 'Member':
        navigate('/training')
        break
      case 'Auditor':
        navigate('/audits')
        break
      default:
        navigate('/dashboard')
        break
    }
  }
}

/**
 * Trigger sidebar resize event
 */
export function triggerSidebarResize() {
  const sidebarWrapper = document.getElementById('kt_app_sidebar_menu_wrapper')
  if (sidebarWrapper) {
    const event = new Event('resize')
    window.dispatchEvent(event)
  }
}

/**
 * Common authentication flow for social providers
 */
export async function handleSocialAuth(
  user: User,
  provider: string,
  saveAuth: Function,
  setCurrentUser: Function,
  navigate: Function
) {
  // Check if email is registered with different provider
  const isEmailValid = await checkEmailProvider(user.email || '', provider)
  if (!isEmailValid) {
    throw new Error(`This email is registered with email/password. Please use email/password to log in.`)
  }

  // Fetch user data
  const additionalUserData = await fetchUserDataFromFirestore(user.uid)
  if (!additionalUserData || Object.keys(additionalUserData).length === 0) {
    throw new Error(`No account found. Please sign up first.`)
  }

  // Fetch personal data
  const userPersonaData = await fetchPersonalDataFromFirestore(
    additionalUserData.tenantId,
    user.uid,
    additionalUserData.email
  )
  if (!userPersonaData) {
    throw new Error('User profile not complete. Please contact your administrator.')
  }

  // Save auth data
  saveAuth({
    api_token: user.refreshToken,
    uid: user.uid,
    email: user.email,
    userName: user.displayName || '',
    tenantId: additionalUserData.tenantId || '',
    role: userPersonaData.role,
  })

  // Set current user
  setCurrentUser({
    uid: user.uid,
    userName: user.displayName || '',
    api_token: user.refreshToken || '',
    email: user.email || '',
    pic: user.photoURL || '',
    role: userPersonaData.role,
    tenantId: additionalUserData.tenantId || '',
    account_type: userPersonaData.account_type,
  })

  // Create activity log
  await createActivityLog(user, additionalUserData.tenantId, additionalUserData)

  // Navigate user
  navigateUserByRole(userPersonaData, navigate)
}
